import { ReactNode, useCallback } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { Resp, SuccessResp } from '@paradigm/api/src';
import { PlatformState } from '@paradigm/api/types';

import {
  FetcherState,
  useFetcher,
} from '#/positions/contexts/abstractions/useFetcher';
import { usePlatformStateWebsocket } from './websocket';
import { usePlatformStateSubscriberStatus } from './subscriber';
import { PlatformStateNotification, PlatformStateWebSocket } from './types';
import { PlatformStateActions } from './store';

export const [usePlatformStateFetcherState, PlatformStateFetcherContext] =
  createSafeContext<FetcherState>('PlatformStateFetcherState');

const channel = 'platform_state';

interface Props {
  children: ReactNode;
  usePlatformStateActions: () => PlatformStateActions;
  fetchSnapshot: () => Promise<Resp<PlatformState>>;
}

export default function PlatformStateFetcherProvider({
  children,
  usePlatformStateActions,
  fetchSnapshot,
}: Props) {
  const ws = usePlatformStateWebsocket();
  const subStatus = usePlatformStateSubscriberStatus();
  const { setState } = usePlatformStateActions();

  const applySnapshot = useCallback(
    (result: SuccessResp<PlatformState>) => setState({ data: result.data }),
    [setState],
  );

  const applyWSNotification = useCallback(
    (notif: PlatformStateNotification) => {
      setState({ data: notif.params.data });
    },
    [setState],
  );

  const state = useFetcher<
    PlatformStateWebSocket,
    PlatformStateNotification,
    PlatformState
  >(
    ws,
    channel,
    subStatus.status,
    applySnapshot,
    applyWSNotification,
    fetchSnapshot,
  );

  return (
    <PlatformStateFetcherContext value={state}>
      {children}
    </PlatformStateFetcherContext>
  );
}
